import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { Feather } from '@expo/vector-icons';

import { GET_MOBILE_PROCEDURES } from '../../graphql/procedure';
import { Text } from '../../components';
import { Card, EmptyState, Screen } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	getProcedureResponsibleName,
} from '../../../common/utils/people/procedureDocumentIntro';

const ProcedureListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [isRefreshing, setIsRefreshing] = useState(false);
	const {
		data,
		error,
		loading,
		refetch,
	} = useQuery(GET_MOBILE_PROCEDURES, {
		variables: {
			page: 0,
			perPage: 100,
		},
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});

	const procedures = useMemo(() => (
		data?.getMobileProcedureDocuments?.items || []
	), [data?.getMobileProcedureDocuments?.items]);

	const refresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetch]);

	const openProcedure = useCallback((procedure) => {
		navigation.navigate('ProcedureDetailView', {
			id: procedure.id,
			document: procedure,
		});
	}, [navigation]);

	return (
		<Screen
			heading="Procedures"
			subheading="Published operating procedures available to your state."
			isLoading={Boolean(loading && !procedures.length)}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refresh}
					tintColor={colors.accent}
				/>
			)}
		>
			{error ? (
				<EmptyState
					tone="error"
					title="Unable to load procedures"
					message="Pull down to try again."
					actionLabel="Try again"
					onAction={refresh}
				/>
			) : null}

			{!error && !procedures.length ? (
				<EmptyState
					icon="file-text"
					title="No procedures available"
					message="Published procedures for your state will appear here."
				/>
			) : null}

			{procedures.length > 0 && (
				<View style={styles.list}>
					{procedures.map((procedure) => {
						const responsibleName = getProcedureResponsibleName(procedure);
						const publishedLabel = procedure?.publishedAt
							? moment(procedure.publishedAt).format('DD MMM YYYY')
							: '';
						const title = procedure.title || 'Procedure';
						const responsibleLabel = responsibleName ? `Responsible: ${responsibleName}` : 'Responsible: Not set';
						const footer = [
							publishedLabel ? `Published ${publishedLabel}` : 'Published',
							procedure?.version ? `Version ${procedure.version}` : 'Current version',
						].join(' · ');

						return (
							<Card
								key={procedure.id}
								tone="raised"
								onPress={() => openProcedure(procedure)}
								accessibilityLabel={[title, responsibleLabel, footer].join(', ')}
							>
								<View style={styles.itemRow}>
									<View style={styles.itemContent}>
										<Text style={styles.itemTitle}>{title}</Text>
										<Text style={styles.itemMeta}>{responsibleLabel}</Text>
										<Text style={styles.itemFooter}>{footer}</Text>
									</View>
									<Feather name="chevron-right" size={22} color={colors.iconMuted} />
								</View>
							</Card>
						);
					})}
				</View>
			)}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	list: {
		gap: space.sm,
	},
	itemRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	itemContent: {
		flex: 1,
		gap: space.xs,
	},
	itemTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	itemMeta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemFooter: {
		...type.caption,
		color: colors.textFaint,
	},
});

export default ProcedureListView;
